// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

'use strict';

import { TokenManager, TokenManagerFactory } from '@protolabs/enterprise-token-manager';
import tokenManagerConfig from '@/tokenManagerConfig';
import cookie from 'js-cookie';
import { getQueryStringValue } from '@/utils/queryUtils';

// allows us to split the guest/main flow without extra custom code
// vite allows us to dynamically import these modules as an async method
// and will only render one or the other depending on which gets called
const guestInit = async () => await import('./guest');
const mainInit = async () => await import('./main');

/**
 * Invoke on a redirect handshake back to commerce.
 */
const completeAuth = () => {
	const tokenManager = TokenManagerFactory.getOrCreateTokenManager(tokenManagerConfig);
	tokenManager
		.completeAuthenticationAsync()
		.then((activeRoute) => {
			window.location.replace(activeRoute);
		})
		.catch((error) => {
			console.error(error);
			window.location.replace('/');
		});
};

const demoKey = 'demo';
const continueAsGuestKey = 'cag';
const continueAsGuestPath = '/continue-as-guest';

const getRelativeUri = (absoluteUri: any) =>
	`${absoluteUri.pathname}${absoluteUri.search}${absoluteUri.hash}`;

const createContinueAsGuestRedirectUri = () => {
	const currentLocation = new URL(window.location.href);
	return `${currentLocation.origin}${continueAsGuestPath}`;
};

const createContinueAsGuestSessionStorageKey = () =>
	`commerce:user-continued-as-guest-on-quote:${window.quoteId}`;
const getUserContinuedAsGuestFromStorage = () =>
	!!sessionStorage.getItem(createContinueAsGuestSessionStorageKey());
const saveUserContinuedAsGuestToStorage = () => {
	if (window.quoteId) {
		sessionStorage.setItem(createContinueAsGuestSessionStorageKey(), '1');
	}
};

const continueAsGuestToUrlStorageKey = `commerce:continue-as-guest-to-url`;
const getContinueAsGuestToUrl = () => sessionStorage.getItem(continueAsGuestToUrlStorageKey);
const setContinueAsGuestToUrl = (value: any) =>
	sessionStorage.setItem(continueAsGuestToUrlStorageKey, value);
const clearContinueAsGuestToUrl = () => sessionStorage.removeItem(continueAsGuestToUrlStorageKey);

/**
 * When an unauthenticated user attempts to access a publically available Quote
 * we want to prompt them to sign in first before allowing them to access the quote. This
 * is done to encourage signups. Requirements call for an inline login form alongside the
 * Continue as Guest option. To meet this requirement we must send them to Identity, requiring
 * a protocol to be established between the services. This function captures the interaction.
 *
 * The procedure is as follows:
 * 1. Commerce recognizes an unauthenticated user is attempting to access a publicly available
 *    quote and executes this function;
 * 2. Commerce saves the current url to session storage with cag=1 added to the query string.
 * 3. Commerce starts authentation, passing a /continue-as-guest redirect url to Identity. This
 *    manifests as a query string parameter named "cag";
 * 4. Identity receives this URL with the "cag" parameter and presents an alternate sign-in form
 *    with an option to continue as guest;
 * 5. At this point the user may sign in as normal OR choose to Continue as a Guest. When they
 *    continue as guest the user is redirected back to the /continue-as-guest path where Commerce
 *    pulls the saved url and replaces the current.
 * 6. Commerce once again recognizes the unauthenticated user is attempting to access a publicly
 *    available quote, this time with the "cag" parameter, the presence of which informs Commerce
 *    to allow the guest through to the quote;
 * 7. Commerce strips the "cag" query string parameter from the URI and stores the fact that the
 *    user opted to continue as guest on this quote to sessionStorage (allowing the user to refresh
 *    the page without being prompted to continue as guest again);
 * 8. Commerce loads the guest experience.
 *
 * @param {TokenManager} tokenManager the token manager instance
 * @return {Promise}
 */
export const runOriginalGuestSignInFlow = (tokenManager: TokenManager) => {
	const currentLocation = new URL(decodeURIComponent(window.location.href));
	const currentLocationHasContinueAsGuestSearchParam =
		currentLocation.searchParams.has(continueAsGuestKey);
	if (currentLocationHasContinueAsGuestSearchParam || getUserContinuedAsGuestFromStorage()) {
		saveUserContinuedAsGuestToStorage();
		if (currentLocationHasContinueAsGuestSearchParam) {
			currentLocation.searchParams.delete(continueAsGuestKey);
			window.history.replaceState({}, document.title, getRelativeUri(currentLocation));
		}

		guestInit().then((mod) => mod.loadApp());

		return Promise.resolve(true);
	} else {
		currentLocation.searchParams.append(continueAsGuestKey, '1');
		setContinueAsGuestToUrl(currentLocation.href);
		const cagRedirect = createContinueAsGuestRedirectUri();
		const lang = cookie.get('locale');
		const getaquote = getQueryStringValue('getaquote');

		const options = {
			...(lang ? { lang } : {}),
			...(getaquote ? { getaquote } : {}),
			...(cagRedirect ? { [continueAsGuestKey]: cagRedirect } : {}),
		};

		return tokenManager.startAuthenticationAsync(options);
	}
};

export const runSignInFlow = (tokenManager: TokenManager) => {
	const lang = cookie.get('locale');
	const getaquote = getQueryStringValue('getaquote');

	const currentLocation = new URL(decodeURIComponent(window.location.href));
	const cagValue = currentLocation.searchParams.get(continueAsGuestKey);

	const extraLoginParams = {
		...(window.guestUserFeatureFlagEnabled && window.isPublic
			? { [continueAsGuestKey]: cagValue ?? '1' }
			: {}),
		...(window.guestUserFeatureFlagEnabled && window.isDemoQuote ? { [demoKey]: true } : {}),
	};

	const options = {
		...(lang ? { lang } : {}),
		...(getaquote ? { getaquote } : {}),
		...extraLoginParams,
	};

	return tokenManager.startAuthenticationAsync(options);
};

export const runAuthFlow = (tokenManager: TokenManager) => {
	if (
		window.guestUserFeatureFlagEnabled &&
		['guest', 'demo'].indexOf(tokenManager.claims.protolabs_user_type) > -1
	) {
		guestInit().then((mod) => mod.loadApp());
	} else {
		mainInit().then((mod) => mod.loadApp());
	}
	return Promise.resolve(true);
};

/**
 * Invoke to conditionally load the main app, guest app. Will trigger redirect
 * to sign in if the user is not authenticated.
 */
export const loadAppOrGuest = () => {
	if (
		// demo quote is going through auth flow if guestUserFeatureFlagEnabled is true
		(!window.guestUserFeatureFlagEnabled && window.isDemoQuote) ||
		window.location.pathname.toLowerCase() === '/contactus'
	) {
		guestInit().then((mod) => mod.loadApp());
		return;
	}

	const tokenManager = TokenManagerFactory.getOrCreateTokenManager(tokenManagerConfig);
	tokenManager
		.initializeAsync()
		.then((isAuthenticated: boolean) => {
			if (isAuthenticated) {
				return runAuthFlow(tokenManager);
			} else if (window.isPublic && !window.guestUserFeatureFlagEnabled) {
				return runOriginalGuestSignInFlow(tokenManager);
			} else {
				return runSignInFlow(tokenManager);
			}
		})
		.catch((error) => {
			console.error(error);
			window.location.replace('/');
		});
};

const toUrl = getContinueAsGuestToUrl();
clearContinueAsGuestToUrl();

if (
	window.location.href.toLowerCase().indexOf(tokenManagerConfig?.redirectUri?.toLowerCase()) === 0
) {
	// If handshaking, then complete auth and send the user to their original entry url
	completeAuth();
} else if (window.location.pathname.toLowerCase() === continueAsGuestPath) {
	window.location.replace(toUrl || '/');
} else {
	loadAppOrGuest();
}
